import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";
import { XMarkIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { rfqMoveToOrder, resetStatus, fetchFinanceManagers, fetchFinanceMembers, setEmptyFinanceMembers } from "@/store/slices/rfqSlice";
import { getSettingData } from "@/store/slices/settingSlice";
import Toaster from "@/components/Toaster";
import FilePreview from "@/components/FilePreview";
import { useDispatch, useSelector } from 'react-redux';
import ShowLoader from "@/components/Loader";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";


const generatePONumber = () => {
    return "PO" + Math.floor(100000 + Math.random() * 900000);
};

// NEW: regexes for notes validation (same as AssignSalesManagerModal)
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const priorities = [
    { label: "High", value: "high" },
    { label: "Medium", value: "medium" },
    { label: "Low", value: "low" },
];

const schema = yup.object().shape({
    purchase_order_number: yup
        .string()
        .required("purchase_order_number_required")
        .min(3, "purchase_order_number_min")
        .max(10, "purchase_order_number_max"),

    finance_manager_id: yup
        .string()
        .required("finance_manager_required"),

    finance_member_id: yup
        .string()
        .nullable(),

    purchase_order_file: yup
        .mixed()
        .required("purchase_order_file_required")
        .test("fileType", "purchase_order_file_type_error", (value) => {
            if (!value) return false;

            const allowedExtensions = [".pdf", ".webp", ".jpg", ".jpeg", ".png"];
            const allowedMimeTypes = [
                "application/pdf",
                "image/webp",
                "image/jpeg",
                "image/jpg",
                "image/png"
            ];

            if (typeof value === "string") {
                return allowedExtensions.some(ext => value.toLowerCase().endsWith(ext));
            }
            return allowedMimeTypes.includes(value.type);
        }),

    // NEW: deadline / priority / commission_type / notes
    deadline: yup.string().required("deadline_required"),
    priority: yup.string().required("priority_required"),
    commission_type: yup.string().required("commission_type_required"),
    notes: yup.string()
        .required("note_required")
        .matches(notBlankRegex, "note_empty_error")
        .matches(noHTMLRegex, "HTML tags are not allowed")
        .matches(noCodeRegex, "Code content is not allowed")
        .matches(noConsecutiveSpecialChars, "Note cannot contain consecutive special characters")
        .matches(noLeadingTrailingPunct, "Note cannot start or end with punctuation")
        .min(2, "note_min_length_error")
        .max(100, "note_max_length_error"),

    // NEW: Finance Manager commission — always required (manager is a required field)
    commission_finance_manager: yup
        .number()
        .typeError("commission_value_required#{\"role\":\"Finance Manager\"}")
        .min(0, "commission_min_value_error")
        .when("commission_type", {
            is: "percentage",
            then: (s) => s.max(100, "commission_percentage_max_error"),
            otherwise: (s) => s.max(99999, "commission_flat_max_error"),
        }),

    // NEW: Finance Rep commission — required only when finance_member_id is selected
    commission_finance_rep: yup
        .number()
        .nullable()
        .when("finance_member_id", {
            is: (value) => value,
            then: (schema) =>
                schema
                    .typeError("commission_value_required#{\"role\":\"Finance Rep\"}")
                    .required("commission_value_required#{\"role\":\"Finance Rep\"}")
                    .min(0, "commission_min_value_error")
                    .when("commission_type", {
                        is: "percentage",
                        then: (s) => s.max(100, "commission_percentage_max_error"),
                        otherwise: (s) => s.max(99999, "commission_flat_max_error"),
                    }),
            otherwise: (schema) => schema.notRequired(),
        }),

    // NEW: Finance Manager timeslot — always required
    time_slot_from_finance_manager: yup
        .string()
        .required("timeslot_required#{\"role\":\"Finance Manager\"}")
        .test("from>=now", "timeslot_past_error#{\"role\":\"Finance Manager\"}", function (v) {
            if (!v) return true;
            return new Date(v).getTime() >= new Date().getTime();
        })
        .test("from<=deadline", "timeslot_exceed_deadline_error#{\"role\":\"Finance Manager\",\"direction\":\"From\"}", function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        }),

    time_slot_to_finance_manager: yup
        .string()
        .when("time_slot_from_finance_manager", {
            is: (value) => !!value,
            then: (schema) =>
                schema
                    .required("timeslot_required#{\"role\":\"Finance Manager\"}")
                    .test(
                        "to<=deadline",
                        "timeslot_exceed_deadline_error#{\"role\":\"Finance Manager\",\"direction\":\"To\"}",
                        function (v) {
                            const deadline = this.parent?.deadline;
                            if (!v || !deadline) return true;
                            return new Date(v).getTime() <= new Date(deadline).getTime();
                        }
                    )
                    .test(
                        "to>=from",
                        "timeslot_to_before_from_error#{\"role\":\"Finance Manager\"}",
                        function (v) {
                            const from = this.parent?.time_slot_from_finance_manager;
                            if (!v || !from) return true;
                            return new Date(v).getTime() >= new Date(from).getTime();
                        }
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),

    // NEW: Finance Rep timeslot — required only when finance_member_id is selected
    time_slot_from_finance_rep: yup
        .string()
        .nullable()
        .when("finance_member_id", {
            is: (value) => !!value,
            then: (schema) =>
                schema
                    .required("timeslot_required#{\"role\":\"Finance Team Member\"}")
                    .test(
                        "from>=now",
                        "timeslot_past_error#{\"role\":\"Finance Team Member\"}",
                        function (v) {
                            if (!v) return true;
                            return new Date(v).getTime() >= new Date().getTime();
                        }
                    )
                    .test(
                        "from<=deadline",
                        "timeslot_exceed_deadline_error#{\"role\":\"Finance Team Member\",\"direction\":\"From\"}",
                        function (v) {
                            const deadline = this.parent?.deadline;
                            if (!v || !deadline) return true;
                            return new Date(v).getTime() <= new Date(deadline).getTime();
                        }
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),

    time_slot_to_finance_rep: yup
        .string()
        .nullable()
        .when("finance_member_id", {
            is: (value) => !!value,
            then: (schema) =>
                schema.when("time_slot_from_finance_rep", {
                    is: (from) => !!from,
                    then: (s) =>
                        s.required("timeslot_required#{\"role\":\"Finance Team Member\"}")
                            .test(
                                "to<=deadline",
                                "timeslot_exceed_deadline_error#{\"role\":\"Finance Team Member\",\"direction\":\"To\"}",
                                function (v) {
                                    const deadline = this.parent?.deadline;
                                    if (!v || !deadline) return true;
                                    return new Date(v).getTime() <= new Date(deadline).getTime();
                                }
                            )
                            .test(
                                "to>=from",
                                "timeslot_to_before_from_error#{\"role\":\"Finance Team Member\"}",
                                function (v) {
                                    const from = this.parent?.time_slot_from_finance_rep;
                                    if (!v || !from) return true;
                                    return new Date(v).getTime() >= new Date(from).getTime();
                                }
                            ),
                    otherwise: (s) => s.notRequired(),
                }),
            otherwise: (schema) => schema.notRequired(),
        }),
});

export function MoveToOrdersModal({ isOpen, onClose, rfq_id, customer_uploaded_po = null }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [leaveConfirmation, setLeaveConfirmation] = useState(null);
    const { t } = useTranslation('rfq');
    const { t: ct } = useTranslation("common");

    const { loading, rfq_details, finance_managers, finance_members } = useSelector((state) => state.rfq);
    const { settings } = useSelector((state) => state.setting); // NEW

    const { register, handleSubmit, setValue, getValues, watch, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            rfq_id: rfq_id,
            purchase_order_number: "",
            finance_manager_id: "",
            finance_member_id: "",
            purchase_order_file: null,
            // NEW
            commission_type: "percentage",
            notes: null,
            deadline: "",
            priority: "",
            time_slot_from_finance_manager: "",
            time_slot_to_finance_manager: "",
            time_slot_from_finance_rep: "",
            time_slot_to_finance_rep: "",
        }
    });

    const selectedFinanceManager = watch("finance_manager_id");
    const purchase_order_file = watch('purchase_order_file');

    // NEW
    const commissionType = watch("commission_type");
    const deadlineValue = watch("deadline");
    const isFinanceMemberSelected = !!watch("finance_member_id");

    // NEW: same list-driven UI pattern as AssignSalesManagerModal
    const defaultCommissions = [
        { text: t("commission_finance_rep_label"), key: "commission_finance_rep", fromKey: "time_slot_from_finance_rep", toKey: "time_slot_to_finance_rep" },
        { text: t("commission_finance_manager_label"), key: "commission_finance_manager", fromKey: "time_slot_from_finance_manager", toKey: "time_slot_to_finance_manager" },
    ];

    const now = new Date();
    const minDateTime = now.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM

    // Fetch Finance Managers when the modal opens
    useEffect(() => {
        if (isOpen) {
            dispatch(fetchFinanceManagers());
            dispatch(getSettingData()); // NEW: fetch settings for default commission
        }
    }, [isOpen, dispatch]);

    // Fetch Finance Members when a Finance Manager is selected
    useEffect(() => {
        if (selectedFinanceManager) {
            dispatch(fetchFinanceMembers(selectedFinanceManager));
            setValue("finance_member_id", ""); // Reset member selection when manager changes
        } else {
            dispatch(setEmptyFinanceMembers());
            setValue("finance_member_id", "");
        }
    }, [selectedFinanceManager, dispatch, setValue]);

    useEffect(() => {
        if (isOpen) {
            // Read purchase_order_number directly from rfq_details (API response is under .data)
            const apiPoNumber =
                rfq_details?.purchase_order_number ||
                rfq_details?.data?.purchase_order_number;

            if (apiPoNumber) {
                setValue("purchase_order_number", apiPoNumber, { shouldValidate: true });
            }

            if (customer_uploaded_po && customer_uploaded_po?.url) {
                setValue("purchase_order_file", customer_uploaded_po.url, { shouldValidate: true });
            }
        }
    }, [isOpen, setValue, customer_uploaded_po, rfq_details]);

    // NEW: default commission values from settings (percentage_commission / flat_commission)
    useEffect(() => {
        if (!settings) return;

        const value =
            commissionType === "percentage"
                ? settings.percentage_commission
                : settings.flat_commission;

        reset((prev) => ({
            ...prev,
            commission_finance_manager: value,
            commission_finance_rep: value,
        }));
    }, [settings, commissionType, reset]);

    // NEW: when finance member is cleared, clear its timeslots too
    useEffect(() => {
        if (!isFinanceMemberSelected) {
            setValue('time_slot_from_finance_rep', "");
            setValue('time_slot_to_finance_rep', "");
        }
    }, [isFinanceMemberSelected, setValue]);

    // NEW: auto-fill Finance Team Member "To" slot with the deadline once both
    // finance_member_id and deadline are selected (same pattern as AssignSalesManagerModal)
    useEffect(() => {
        const deadline = watch("deadline");

        if (!deadline) return;

        setValue("time_slot_to_finance_manager", deadline, {
            shouldValidate: true,
        });
    }, [watch("deadline"), setValue]);

    const onSubmit = async (data) => {

        const payload = {
            rfq_id,
            purchase_order_number: data.purchase_order_number,
            finance_manager_id: data.finance_manager_id || null,
            finance_member_id: data.finance_member_id || null,
            purchase_order_file: data.purchase_order_file,

            // NEW
            deadline: data.deadline,
            priority: data.priority,
            commission_type: data.commission_type,
            notes: data.notes,
            commission_finance_manager: data.commission_finance_manager,
            commission_finance_rep: data.commission_finance_rep,
            time_slot_from_finance_manager: data.time_slot_from_finance_manager,
            time_slot_to_finance_manager: data.time_slot_to_finance_manager,
            time_slot_from_finance_rep: data.time_slot_from_finance_rep,
            time_slot_to_finance_rep: data.time_slot_to_finance_rep,
        };

        try {

            const result = await dispatch(
                rfqMoveToOrder(payload)
            ).unwrap();

            if (result?.status) {

                Toaster.success(t('move_to_order_success'));

                dispatch(resetStatus());

                reset();

                onClose();

                navigate('/dashboard/rfqs');

            } else if (!result?.status && result?.data?.users_on_leave) {
                setLeaveConfirmation(result.data.users_on_leave);
                Toaster.error(result.message || ct("assignOnLeaveError"));
                dispatch(resetStatus());
            } else {

                Toaster.error(
                    result.message || ct("somethingWentWrong")
                );

                dispatch(resetStatus());
            }

        } catch (err) {

            Toaster.error(err?.message || t('move_to_order_failed'));

            dispatch(resetStatus());
        }
    };

    const handleForceAssign = async () => {
        try {
            const data = getValues();

            const payload = {
                rfq_id,
                purchase_order_number: data.purchase_order_number,
                finance_manager_id: data.finance_manager_id || null,
                finance_member_id: data.finance_member_id || null,
                purchase_order_file: data.purchase_order_file,
                force_assign: true,

                // NEW
                deadline: data.deadline,
                priority: data.priority,
                commission_type: data.commission_type,
                notes: data.notes,
                commission_finance_manager: data.commission_finance_manager,
                commission_finance_rep: data.commission_finance_rep,
                time_slot_from_finance_manager: data.time_slot_from_finance_manager,
                time_slot_to_finance_manager: data.time_slot_to_finance_manager,
                time_slot_from_finance_rep: data.time_slot_from_finance_rep,
                time_slot_to_finance_rep: data.time_slot_to_finance_rep,
            };
            const result = await dispatch(rfqMoveToOrder(payload)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || t('move_to_order_success'));
                dispatch(resetStatus());
                reset();
                onClose();
                setLeaveConfirmation(null);
                navigate('/dashboard/rfqs');
            } else {
                setLeaveConfirmation(null);
                Toaster.error(result.message || ct("somethingWentWrong"));
                dispatch(resetStatus());
            }
        } catch (err) {
            setLeaveConfirmation(null);
            Toaster.error(err?.message || t('move_to_order_failed'));
            dispatch(resetStatus());
        }
    }

    const handleBoxClick = () => {
        document.getElementById("po-pdf").click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setValue("purchase_order_file", file, { shouldValidate: true });
    };

    useEffect(() => {
        const managerFrom = watch("time_slot_to_finance_rep");

        if (managerFrom) {
            setValue("time_slot_from_finance_manager", managerFrom, {
                shouldValidate: true,
            });
        }
    }, [watch("time_slot_to_finance_rep"), setValue]);

    return (
        <Dialog open={isOpen} handler={onClose} size="sm" className="rounded-xl">
            {loading && <ShowLoader />}

            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-xl font-bold text-gray-900">{t('move_to_orders_title')}</h2>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogBody className="px-6 max-h-[65vh] overflow-y-auto space-y-6">

                    {/* PO Number */}
                    <div>
                        <label className="block text-sm font-medium mb-2 capitalize">
                            {t('purchase_order_number_input_label')} <span className="text-red-600">*</span>
                        </label>

                        <input
                            type="text"
                            {...register("purchase_order_number")}
                            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                            placeholder={t('purchase_order_number_placeholder')}
                        />
                        {errors.purchase_order_number && (
                            <p className="text-red-600 text-sm mt-1">
                                {t(errors.purchase_order_number.message)}
                            </p>
                        )}
                    </div>

                    {/* Finance Manager Dropdown */}
                    <div>
                        <label className="block text-sm font-medium mb-2 capitalize">
                            {t('finance_manager_label')} <span className="text-red-600">*</span>
                        </label>
                        <div className="relative">
                            <select
                                {...register("finance_manager_id")}
                                className="w-full p-3 pr-10 border border-gray-300 bg-white rounded-md focus:ring-2 focus:ring-yellow-500 appearance-none"
                            >
                                <option value="">{t('select_finance_manager_placeholder')}</option>
                                {finance_managers?.map((manager) => (
                                    <option key={manager.value} value={manager.value}>
                                        {manager.label}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                </svg>
                            </div>
                        </div>
                        {errors.finance_manager_id && (
                            <p className="text-red-600 text-sm mt-1">
                                {t(errors.finance_manager_id.message)}
                            </p>
                        )}
                    </div>

                    {/* Finance Member Dropdown */}
                    <div>
                        <label className="block text-sm font-medium mb-2 capitalize">
                            {t('finance_team_member_label')}
                        </label>
                        <div className="relative">
                            <select
                                {...register("finance_member_id")}
                                disabled={!selectedFinanceManager}
                                className="w-full p-3 pr-10 border border-gray-300 bg-white rounded-md focus:ring-2 focus:ring-yellow-500 appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <option value="">{t('select_finance_team_member_placeholder')}</option>
                                {finance_members?.map((member) => (
                                    <option key={member.value} value={member.value}>
                                        {member.label}
                                    </option>
                                ))}
                            </select>
                            <div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 ${!selectedFinanceManager ? 'text-gray-400' : 'text-gray-500'}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                </svg>
                            </div>
                        </div>
                        {errors.finance_member_id && (
                            <p className="text-red-600 text-sm mt-1">
                                {t(errors.finance_member_id.message)}
                            </p>
                        )}
                    </div>

                    {/* NEW: Deadline & Priority */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2 capitalize">
                                {t('rfq_deadline_label')} <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                min={minDateTime}
                                {...register("deadline")}
                                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                            />
                            {errors.deadline && (
                                <p className="text-red-600 text-sm mt-1">{t(errors.deadline.message)}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2 capitalize">
                                {t('rfq_priority_label')} <span className="text-red-600">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    {...register("priority")}
                                    className="w-full p-3 pr-10 border border-gray-300 bg-white rounded-md focus:ring-2 focus:ring-yellow-500 appearance-none"
                                >
                                    <option value="">{t('select_priority_placeholder')}</option>
                                    {priorities.map((p) => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                    </svg>
                                </div>
                            </div>
                            {errors.priority && (
                                <p className="text-red-600 text-sm mt-1">{t(errors.priority.message)}</p>
                            )}
                        </div>
                    </div>

                    {/* NEW: Commission Section (Finance Manager / Finance Rep) */}
                    <div>
                        <label className="block text-sm font-medium mb-2 capitalize">
                            {t("set_commission_label")}
                        </label>
                        <div className="flex items-center gap-6 mb-3">
                            <label className="flex items-center gap-2 text-sm">
                                <input type="radio" value="percentage" {...register("commission_type")} />
                                {t("percentage_commission_label")}
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="radio" value="flat" {...register("commission_type")} />
                                {t("flat_commission_label")}
                            </label>
                        </div>

                        <div className="space-y-4">
                            {defaultCommissions.map((item, index) => (
                                <div key={index} className="flex flex-col gap-1">
                                    <div className="bg-gray-50 rounded-md">
                                        <div className="flex gap-1 items-center justify-between px-4 py-2">
                                            <p className="text-sm text-gray-800">{item.text}</p>
                                            <div className="flex items-center gap-3 flex-wrap justify-end">
                                                {/* <div className="flex items-center gap-2">
                                                    <input
                                                        step="any"
                                                        type="number"
                                                        min="0"
                                                        {...register(item.key)}
                                                        disabled={item.key === "commission_finance_rep" && !isFinanceMemberSelected}
                                                        className={`w-20 text-center border rounded-md py-1 text-sm ${errors[item.key] ? "border-red-500" : "border-gray-300"
                                                            } ${item.key === "commission_finance_rep" && !isFinanceMemberSelected
                                                                ? "bg-gray-100 cursor-not-allowed"
                                                                : ""
                                                            }`}
                                                    />
                                                    <span className="text-gray-600 text-sm">
                                                        {watch("commission_type") === "percentage" ? "%" : "SAR"}
                                                    </span>
                                                </div> */}
                                                {/* <div className="flex items-center gap-2"> */}
                                                <div className="flex items-center gap-2 flex-wrap md:flex-nowrap justify-end">
                                                    <input
                                                        step="any"
                                                        type="number"
                                                        min="0"
                                                        {...register(item.key)}
                                                        className={`w-20 text-center border rounded-md py-1 text-sm ${errors[item.key] ? "border-red-500" : "border-gray-300"}`}
                                                    />
                                                    <span className="text-gray-600 text-sm">
                                                        {watch("commission_type") === "percentage" ? "%" : "SAR"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-1 items-center justify-between px-4 py-2">
                                            <span className="text-gray-600 text-xs whitespace-nowrap capitalize">
                                                {t("timeslot_label_simple")}
                                            </span>
                                            {/* <div className="flex items-center gap-2"> */}
                                            <div className="flex items-center gap-2 flex-wrap md:flex-nowrap justify-end">
                                                {/* <input
                                                    type="datetime-local"
                                                    disabled={
                                                        !deadlineValue ||
                                                        (item.key === "commission_finance_rep" && !isFinanceMemberSelected)
                                                    }
                                                    min={minDateTime}
                                                    max={deadlineValue || undefined}
                                                    {...register(item.fromKey)}
                                                    className={`w-[175px] text-center border rounded-md py-1 text-sm ${errors[item.fromKey] ? "border-red-500" : "border-gray-300"
                                                        } ${item.key === "commission_finance_rep" && !isFinanceMemberSelected
                                                            ? "bg-gray-100 cursor-not-allowed"
                                                            : ""
                                                        }`}
                                                /> */}
                                                <input
                                                    type="datetime-local"
                                                    disabled={!deadlineValue}
                                                    min={minDateTime}
                                                    max={deadlineValue || undefined}
                                                    {...register(item.fromKey)}
                                                    className={`w-[150px] sm:w-[165px] text-center border rounded-md py-1 text-sm ${errors[item.fromKey] ? "border-red-500" : "border-gray-300"}`}
                                                />
                                                {/* <input
                                                    type="datetime-local"
                                                    disabled={
                                                        !deadlineValue ||
                                                        (item.key === "commission_finance_rep" && !isFinanceMemberSelected)
                                                    }
                                                    min={watch(item.fromKey) || minDateTime}
                                                    max={deadlineValue || undefined}
                                                    {...register(item.toKey)}
                                                    className={`w-[175px] text-center border rounded-md py-1 text-sm ${errors[item.toKey] ? "border-red-500" : "border-gray-300"
                                                        } ${item.key === "commission_finance_rep" && !isFinanceMemberSelected
                                                            ? "bg-gray-100 cursor-not-allowed"
                                                            : ""
                                                        }`}
                                                /> */}
                                                <input
                                                    type="datetime-local"
                                                    disabled={!deadlineValue}
                                                    min={watch(item.fromKey) || minDateTime}
                                                    max={deadlineValue || undefined}
                                                    {...register(item.toKey)}
                                                    className={`w-[150px] sm:w-[165px] text-center border rounded-md py-1 text-sm ${errors[item.toKey] ? "border-red-500" : "border-gray-300"}`}
                                                />
                                                {(watch(item.fromKey) || watch(item.toKey)) && (
                                                    <XCircleIcon
                                                        className="h-5 w-5 text-red-500 cursor-pointer hover:text-red-700 transition-colors"
                                                        onClick={() => {
                                                            setValue(item.fromKey, "", { shouldValidate: true });
                                                            setValue(item.toKey, "", { shouldValidate: true });
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {errors[item.key] && (
                                        <p className="text-red-500 text-xs px-4">{t(errors[item.key].message.split('#')[0], { role: JSON.parse(errors[item.key].message.split('#')[1] || '{}')?.role })}</p>
                                    )}
                                    {(errors[item.fromKey] || errors[item.toKey]) && (
                                        <p className="text-red-500 text-xs px-4">
                                            {t((errors[item.fromKey] || errors[item.toKey])?.message.split('#')[0], { role: JSON.parse((errors[item.fromKey] || errors[item.toKey])?.message.split('#')[1] || '{}')?.role, direction: JSON.parse((errors[item.fromKey] || errors[item.toKey])?.message.split('#')[1] || '{}')?.direction })}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* NEW: Notes */}
                    <div>
                        <label className="block text-sm font-medium mb-1 capitalize">
                            {t("note_label")} <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            rows="3"
                            className="w-full border rounded-lg px-3 py-2"
                            {...register("notes")}
                            placeholder={t("note_placeholder_finance")}
                        ></textarea>
                        {errors.notes && (
                            <p className="text-red-500 text-sm">{t(errors.notes.message)}</p>
                        )}
                    </div>

                    {/* File Upload */}
                    <div>
                        <label className="block text-sm font-medium mb-2 capitalize">
                            {t('upload_pdf_label')} <span className="text-red-600">*</span>
                        </label>

                        <div
                            onClick={handleBoxClick}
                            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-yellow-500 transition"
                        >
                            <p className="text-gray-700 font-medium">{t('upload_pdf_instruction')}</p>
                            <p className="text-xs text-gray-500 mt-1">{t('allowed_file_types_receipt')}</p>

                            <input
                                type="file"
                                id="po-pdf"
                                accept="application/pdf, image/webp, image/jpeg, image/png, image/jpg"
                                onChange={handleFileChange}
                                className="hidden"
                            />

                        </div>
                        {purchase_order_file && (
                            <FilePreview
                                file={purchase_order_file}
                                onRemove={() => {
                                    setValue('purchase_order_file', null);
                                }}
                                isView={false}
                                containerClass="relative w-58 h-40 mt-2 rounded-md overflow-hidden border border-gray-300"
                            />
                        )}

                        {errors.purchase_order_file && (
                            <p className="text-red-600 text-sm mt-1">
                                {t(errors.purchase_order_file.message)}
                            </p>
                        )}
                    </div>

                    {/* NEW: Important Commission Notice (same as sales/procurement modal) */}
                    <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                        <p className="text-sm font-medium text-amber-900">
                            {t("important_notice_title")}
                        </p>
                        <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                            {t("commission_deduction_notice")}
                        </p>
                    </div>

                </DialogBody>
                {leaveConfirmation?.length > 0 && (
                    <div className="mx-6 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-sm">
                        {/* Header with Warning Icon */}
                        <div className="flex items-center gap-2 mb-3">
                            <svg
                                className="h-5 w-5 text-amber-600 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                                />
                            </svg>
                            <h3 className="font-semibold text-amber-800">
                                {t('schedule_conflict_title')}
                            </h3>
                        </div>

                        {/* User List */}
                        <ul className="divide-y divide-amber-200/60 rounded-md bg-amber-100/50 px-3 py-1 mb-3">
                            {leaveConfirmation.map((user) => (
                                <li
                                    key={user.user_id}
                                    className="flex flex-col sm:flex-row sm:items-center justify-between py-2 text-sm gap-1 sm:gap-4"
                                >
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-medium text-gray-900">{user.name}</span>
                                        {(user.user_type || user.role) && (
                                            <span className="inline-flex items-center rounded-md bg-amber-200/70 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                                                {[user.user_type, user.role].filter(Boolean).join(" • ")}
                                            </span>
                                        )}
                                    </div>

                                    <div className="text-xs sm:text-sm font-medium text-amber-700 whitespace-nowrap">
                                        {moment(user.leave_from).format("DD MMM YYYY")} –{" "}
                                        {moment(user.leave_to).format("DD MMM YYYY")}
                                    </div>
                                </li>
                            ))}
                        </ul>

                        {/* Actionable Footer Copy */}
                        <p className="text-sm text-amber-800">
                            <Trans t={t} i18nKey="force_assign_notice">
                                To proceed, select available users or enable <strong className="font-semibold underline decoration-amber-500 underline-offset-2">Force Assign</strong> to override their leave status.
                            </Trans>
                        </p>
                    </div>
                )}

                {/* Footer */}
                <DialogFooter className="flex justify-end gap-3 border-t px-6 py-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300 transition"
                    >
                        {ct('cancel')}
                    </button>

                    <button
                        type="submit"
                        className="px-5 py-2.5 bg-[#065C36] text-white rounded-md font-semibold hover:bg-green-900 transition"
                        disabled={loading}
                    >
                        {t('approve_assign_to_finance_button')}
                    </button>
                    {leaveConfirmation?.length > 0 && (
                        <button
                            type="button"
                            className="px-5 py-2 rounded-lg bg-red-400 hover:bg-red-500 text-white font-medium shadow-sm"
                            onClick={() => handleForceAssign()}
                        >
                            {t('force_assign_button')}
                        </button>
                    )}
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default MoveToOrdersModal;